<template>
  <div class="office-page">
    <!-- Page header -->
    <div class="office-page__header">
      <div class="office-page__header-inner">
        <div class="office-page__header-left">
          <div class="office-page__header-icon">
            <feather-icon
              icon="HomeIcon"
              size="28"
            />
          </div>
          <div>
            <h1 class="office-page__title">
              Office Management
            </h1>
            <p class="office-page__subtitle">
              Manage offices, banking details, and currency conversions
            </p>
          </div>
        </div>
        <!-- <b-button
          variant="light"
          class="office-page__btn-add"
          @click="$router.push({ name: 'add-office' })"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
          <span>Add Office</span>
        </b-button> -->
      </div>
    </div>

    <!-- Quick guide steps -->
    <div class="office-steps">
      <div
        v-for="(step, index) in guideSteps"
        :key="step.title"
        class="office-step"
      >
        <div class="office-step__number">
          {{ index + 1 }}
        </div>
        <div
          class="office-step__icon"
          :class="`office-step__icon--${step.variant}`"
        >
          <feather-icon
            :icon="step.icon"
            size="20"
          />
        </div>
        <div class="office-step__text">
          <span class="office-step__title">{{ step.title }}</span>
          <span class="office-step__desc">{{ step.description }}</span>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <b-card class="office-toolbar-card">
      <b-row class="align-items-center">
        <b-col
          cols="12"
          md="6"
          class="mb-1 mb-md-0"
        >
          <b-input-group class="office-search">
            <b-input-group-prepend is-text>
              <feather-icon
                icon="SearchIcon"
                size="16"
              />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search offices by name, country..."
              @keyup.enter="applySearch"
            />
            <b-input-group-append>
              <b-button
                variant="primary"
                @click="applySearch"
              >
                Search
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex justify-content-md-end gap-50"
        >
          <b-button
            v-if="searchQuery"
            variant="outline-secondary"
            class="office-toolbar-btn"
            @click="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
            Clear
          </b-button>
          <b-button
            variant="outline-primary"
            class="office-toolbar-btn"
            :disabled="dataLoad"
            @click="refreshAll"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'office-spin': dataLoad }"
            />
            Refresh
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Loading -->
    <div
      v-if="dataLoad"
      class="office-loading"
    >
      <b-spinner
        variant="primary"
        type="grow"
      />
      <p>Loading offices...</p>
    </div>

    <!-- Empty state -->
    <b-card
      v-else-if="!offices.length"
      class="office-empty"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="office-empty__icon"
      />
      <h3>No offices found</h3>
      <p>Try adjusting your search or add a new office to get started.</p>
      <b-button
        variant="primary"
        @click="$router.push({ name: 'add-office' })"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
          class="mr-50"
        />
        Add Office
      </b-button>
    </b-card>

    <!-- Office cards -->
    <div
      v-else
      class="office-list"
    >
      <b-card
        v-for="office in offices"
        :key="office.id"
        class="office-card"
        :class="{ 'office-card--expanded': isExpanded(office.id) }"
      >
        <!-- Card header -->
        <div class="office-card__header">
          <div class="office-card__identity">
            <div class="office-card__avatar">
              <feather-icon
                icon="MapPinIcon"
                size="22"
              />
              <span
                class="office-card__flag-badge"
                :title="office.country || ''"
              >
                <img
                  v-if="getCountryFlagSrc(office.country)"
                  :src="getCountryFlagSrc(office.country)"
                  alt="Country Flag"
                  class="office-card__flag-img"
                >
                <feather-icon
                  v-else
                  icon="FlagIcon"
                  size="12"
                />
              </span>
            </div>
            <div>
              <h3 class="office-card__name">
                {{ office.name }}
              </h3>
              <div class="office-card__meta-row">
                <span
                  v-if="office.country"
                  class="office-card__chip"
                >
                  <feather-icon
                    icon="GlobeIcon"
                    size="12"
                  />
                  {{ office.country }}
                </span>
                <b-badge
                  v-if="office.currency"
                  pill
                  variant="light-primary"
                  class="office-card__currency-badge"
                >
                  <feather-icon
                    icon="DollarSignIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ office.currency }}
                </b-badge>
              </div>
            </div>
          </div>

          <div class="office-card__actions">
            <a
              :href="getEditLink(office)"
              target="_blank"
              rel="noopener"
              class="office-action-btn office-action-btn--edit"
              title="Edit office"
            >
              <feather-icon
                icon="Edit3Icon"
                size="16"
              />
            </a>
            <!-- <button
              type="button"
              class="office-action-btn office-action-btn--delete"
              title="Delete office"
              @click="deleteOffice(office.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="16"
              />
            </button> -->
            <b-button
              size="sm"
              :variant="isExpanded(office.id) ? 'outline-primary' : 'primary'"
              class="office-card__toggle-btn"
              @click="toggleExpanded(office.id)"
            >
              <feather-icon
                :icon="isExpanded(office.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="16"
                class="mr-25"
              />
              {{ isExpanded(office.id) ? 'Less' : 'Details' }}
            </b-button>
          </div>
        </div>

        <!-- Leaves balance (always visible summary) -->
        <div
          v-if="office.leaves_balance && office.leaves_balance.length"
          class="office-card__leaves"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-50"
          />
          <span
            v-for="item in office.leaves_balance"
            :key="item.id"
            class="office-leave-chip"
          >
            {{ item.type }}: <strong>{{ item.balance }}</strong>
          </span>
        </div>

        <!-- Expanded panel -->
        <b-collapse
          :id="`office-details-${office.id}`"
          v-model="expandedIds[office.id]"
          class="office-card__body"
        >
          <!-- Bank details -->
          <div class="office-section">
            <h4 class="office-section__title">
              <feather-icon
                icon="CreditCardIcon"
                size="16"
              />
              Banking Information
            </h4>
            <b-row>
              <b-col
                v-for="field in bankFields(office)"
                :key="field.key"
                cols="12"
                md="6"
                lg="4"
                class="mb-1"
              >
                <div class="office-info-tile">
                  <span class="office-info-tile__label">{{ field.label }}</span>
                  <span class="office-info-tile__value">{{ field.value || '—' }}</span>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Currency conversions -->
          <div class="office-section office-section--currency">
            <div class="office-section__head">
              <h4 class="office-section__title">
                <feather-icon
                  icon="RepeatIcon"
                  size="16"
                />
                Currency Conversions
              </h4>
              <b-button
                size="sm"
                variant="primary"
                class="office-currency-add-btn"
                @click="showAddCurrencyConversion(office.id, office.name)"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="mr-25"
                />
                Add Conversion
              </b-button>
            </div>
            <div
              v-if="!currencyConversionsFor(office.id).length"
              class="office-currency-empty"
            >
              <feather-icon
                icon="TrendingUpIcon"
                size="32"
              />
              <p>No conversion rates yet for this office.</p>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="showAddCurrencyConversion(office.id, office.name)"
              >
                Add first rate
              </b-button>
            </div>

            <div
              v-else
              class="office-currency-grid"
            >
              <div
                v-for="item in currencyConversionsFor(office.id)"
                :key="item.id"
                class="office-currency-card"
              >
                <div class="office-currency-card__rate">
                  <span class="office-currency-card__from">{{ item.currency }}</span>
                  <feather-icon
                    icon="ArrowRightIcon"
                    size="14"
                    class="mx-50"
                  />
                  <span class="office-currency-card__to">{{ item.conversion_rate }} {{ office.currency }}</span>
                </div>
                <div class="office-currency-card__actions">
                  <button
                    type="button"
                    class="office-action-btn office-action-btn--edit office-action-btn--sm"
                    title="Edit conversion"
                    @click="editCurrencyConversion(office.id, office.name, item.currency, item.conversion_rate, item.id)"
                  >
                    <feather-icon
                      icon="Edit3Icon"
                      size="14"
                    />
                  </button>
                  <button
                    type="button"
                    class="office-action-btn office-action-btn--delete office-action-btn--sm"
                    title="Delete conversion"
                    @click="showDeleteCurrencyConversion(item.id, office.name)"
                  >
                    <feather-icon
                      icon="Trash2Icon"
                      size="14"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
      </b-card>
    </div>

    <!-- Modals -->
    <delete-currency-conversion-modal
      v-if="deleteCurrencyConversionModal"
      :show-modal="deleteCurrencyConversionModal"
      :office-name="OfficeName"
      :office-id-delete="officeIdDelete"
      @refreshCurrencyConversions="getCurrencyConversion"
      @update:showModal="deleteCurrencyConversionModal = $event"
    />

    <add-currency-conversion-modal
      v-if="showCurrencyConversionModal"
      :show-modal="showCurrencyConversionModal"
      :office-name="OfficeName"
      :office-id="officeId"
      @refreshCurrencyConversions="getCurrencyConversion"
      @update:showModal="showCurrencyConversionModal = $event"
    />

    <edit-currency-conversion-modal
      v-if="editCurrencyConversionModal"
      :show-modal="editCurrencyConversionModal"
      :office-name="OfficeName"
      :office-id="officeId"
      :item-currency="itemCurrency"
      :item-conversion-rate="itemConversionRate"
      :item-id="itemId"
      @refreshCurrencyConversions="getCurrencyConversion"
      @update:showModal="editCurrencyConversionModal = $event"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { isString } from 'lodash'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import DeleteCurrencyConversionModal from './control-office/DeleteCurrencyConversion.vue'
import AddCurrencyConversionModal from './control-office/AddCurrencyConversionModal.vue'
import EditCurrencyConversionModal from './control-office/EditCurrencyConversionModal.vue'

// Load available branch flags without hardcoding imports (safe even if some files are missing)
// eslint-disable-next-line global-require
const branchFlags = require.context('@/assets/images/branchs', false, /\.png$/)

export default {
  components: {
    DeleteCurrencyConversionModal,
    AddCurrencyConversionModal,
    EditCurrencyConversionModal,
  },
  data() {
    return {
      offices: [],
      totalRows: 0,
      dataLoad: false,
      searchQuery: this.$route.query.search || '',
      expandedIds: {},
      CurrencyConversionData: [],
      showCurrencyConversionModal: false,
      deleteCurrencyConversionModal: false,
      editCurrencyConversionModal: false,
      officeIdDelete: '',
      OfficeName: '',
      officeId: '',
      itemCurrency: '',
      itemConversionRate: '',
      itemId: '',
      guideSteps: [
        {
          icon: 'SearchIcon',
          variant: 'primary',
          title: 'Find an office',
          description: 'Use search to quickly locate offices by name or country.',
        },
        {
          icon: 'PlusIcon',
          variant: 'success',
          title: 'Add or edit',
          description: 'Create a new office or open edit to update details.',
        },
        {
          icon: 'RepeatIcon',
          variant: 'warning',
          title: 'Currency rates',
          description: 'Expand an office card to manage conversion rates.',
        },
        {
          icon: 'Trash2Icon',
          variant: 'danger',
          title: 'Remove safely',
          description: 'Delete offices or rates only when you are certain.',
        },
      ],
    }
  },
  watch: {
    '$route.query.search': {
      handler(val) {
        this.searchQuery = val || ''
      },
    },
  },
  mounted() {
    this.searchQuery = this.$route.query.search || ''
    this.fetchOffices(1)
    this.getCurrencyConversion()
  },
  methods: {
    normalizeCountryKey(country) {
      if (!country) return ''
      return String(country)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
    },
    getFlagByFilename(filename) {
      if (!filename) return ''
      try {
        return branchFlags(`./${filename}`)
      } catch (e) {
        return ''
      }
    },
    getCountryFlagSrc(country) {
      const key = this.normalizeCountryKey(country)
      // Mapping matches filenames used in `OurBranches.vue`
      if (key.includes('unitedarabemirates') || key === 'uae') return this.getFlagByFilename('UnitedArabEmirates.png')
      if (key.includes('kingdomofsaudiarabia') || key.includes('saudiarabia') || key === 'ksa') return this.getFlagByFilename('KingdomOfSaudiArabia.png')
      if (key === 'egypt') return this.getFlagByFilename('Egypt.png')
      if (key === 'jordan') return this.getFlagByFilename('Jordan.png')
      if (key === 'turkey') return this.getFlagByFilename('Turkey.png')
      if (key === 'pakistan') return this.getFlagByFilename('Pakistan.png')
      if (key === 'india' || key === 'chennaiindia') return this.getFlagByFilename('India.png')
      if (key === 'china') return this.getFlagByFilename('China.png')
      if (key === 'russia') return this.getFlagByFilename('Russia.png')
      if (key === 'philippines') return this.getFlagByFilename('Philippines.png')
      if (key === 'qatar') return this.getFlagByFilename('Qatar.png')

      return ''
    },
    async fetchOffices(page = 1) {
      this.dataLoad = true
      this.page = page

      try {
        const skip = (page - 1) * this.perPage || 0
        const params = { skip }
        if (this.$route.query.search) {
          params.search = this.$route.query.search
        }

        const response = await axios.get('office', { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const dataKey = Object.keys(responseData.data).find(
            k => !['count', 'length', 'external_statuses', 'internal_statuses'].includes(k),
          )
          this.offices = dataKey ? responseData.data[dataKey] : []
          this.totalRows = responseData.data.count || responseData.data.length || 0
        }
      } catch (error) {
        if (error.response?.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server Error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.dataLoad = false
      }
    },

    async getCurrencyConversion() {
      const res = await axios.get('currency')
      this.CurrencyConversionData = decryptData(res.data.payload).data.data
    },

    refreshAll() {
      this.fetchOffices(this.page)
      this.getCurrencyConversion()
    },

    applySearch() {
      const query = { ...this.$route.query }
      if (this.searchQuery) {
        query.search = this.searchQuery
      } else {
        delete query.search
      }
      this.$router.push({ query }).then(() => {
        this.fetchOffices(1)
      })
    },

    clearSearch() {
      this.searchQuery = ''
      const query = { ...this.$route.query }
      delete query.search
      this.$router.push({ query }).then(() => {
        this.fetchOffices(1)
      })
    },

    currencyConversionsFor(officeId) {
      return this.CurrencyConversionData.filter(item => item.office_id === officeId)
    },

    isExpanded(officeId) {
      return !!this.expandedIds[officeId]
    },

    toggleExpanded(officeId) {
      this.$set(this.expandedIds, officeId, !this.expandedIds[officeId])
    },

    getEditLink(office) {
      return this.$router.resolve({
        name: 'edit-office',
        params: { id: office.id, name: office.name || '' },
      }).href
    },

    bankFields(office) {
      return [
        { key: 'iban', label: 'IBAN', value: office.iban },
        { key: 'swift_code', label: 'Swift Code', value: office.swift_code },
        { key: 'bank_account_number', label: 'Account Number', value: office.bank_account_number },
        { key: 'bank_account_name', label: 'Account Name', value: office.bank_account_name },
        { key: 'check_name', label: 'Check Name', value: office.check_name },
      ]
    },

    deleteOffice(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#7367f0',
        cancelButtonColor: '#ea5455',
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.isConfirmed) {
          axios
            .delete(`office/${id}`)
            .then(() => {
              this.$swal('Deleted!', 'Deleted Successfully.', 'success')
              this.fetchOffices(this.page)
            })
            .catch(error => {
              const payload = decryptData(error.response.data.payload).message
              let errorMessage = ''
              if (isString(payload)) {
                errorMessage = payload
              } else {
                const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
                errorMessage = firstArrayKey ? payload[firstArrayKey][0] : 'An unknown error occurred'
              }
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: errorMessage,
                  icon: 'BellIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },

    showAddCurrencyConversion(id, name) {
      this.showCurrencyConversionModal = true
      this.OfficeName = name
      this.officeId = id
    },

    showDeleteCurrencyConversion(id, name) {
      this.deleteCurrencyConversionModal = true
      this.OfficeName = name
      this.officeIdDelete = id
    },

    editCurrencyConversion(id, name, currency, conversionRate, itemId) {
      this.editCurrencyConversionModal = true
      this.OfficeName = name
      this.officeId = id
      this.itemCurrency = currency
      this.itemConversionRate = conversionRate
      this.itemId = itemId
    },
  },
}
</script>

<style lang="scss" scoped>
@import './office.scss';
</style>
